import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, KeyRound, Sparkles, X } from 'lucide-react';
import { OrbitSteps } from '../components/OrbitSteps';

/*
 * Direct entry: the public front door for a company arriving without a
 * partner.
 *
 * It is a doorway, not a flow. Every route out of here leads to something
 * that already exists: setting up a company runs the same /auth/signup and
 * lands in Orbit's intake, and joining redeems the invite the company already
 * issued. Nothing on this page creates an account, a company, or a membership
 * by itself.
 *
 * The steps on the right are the shared card, so the front door and the
 * sign-in page describe the same journey in the same words.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const emailLooksReal = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

// People paste the whole invite link as often as they type the code out of it.
const codeFrom = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed.includes('/')) return trimmed;
  return trimmed.split('?')[0].split('/').filter(Boolean).pop() || '';
};

// A Solution Partner's client link pasted where an invitation code was
// expected. It is not an invitation to an existing company: it starts a new
// one under that partner, so it is routed into company setup instead.
const referralFrom = (raw: string): string => {
  const trimmed = raw.trim();
  try {
    const url = new URL(trimmed, window.location.origin);
    return url.searchParams.get('ref') || '';
  } catch {
    return '';
  }
};

const JoinCompany: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [noCode, setNoCode] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The code is checked here rather than on the accept page, so a wrong one
  // fails in front of the person who typed it instead of on a dead screen.
  const review = async () => {
    const referral = referralFrom(code);
    if (referral) {
      navigate(`/signup?ref=${encodeURIComponent(referral)}`);
      return;
    }
    const token = codeFrom(code);
    if (!emailLooksReal(email)) {
      setError('Enter the work email your company invited.');
      return;
    }
    if (!token) {
      setError('Enter the invitation code from your invite.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/invite/${encodeURIComponent(token)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'This invitation is invalid, used, or expired.');
        return;
      }
      if (data.email && data.email.toLowerCase() !== email.trim().toLowerCase()) {
        setError(`That invitation was sent to a different address. Use the one your company invited.`);
        return;
      }
      navigate(`/invite/${encodeURIComponent(token)}`);
    } catch {
      setError('Could not check that invitation. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/30 px-5 backdrop-blur-sm"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-[520px] overflow-hidden rounded-panel bg-white shadow-[0_24px_60px_rgba(24,25,29,0.2)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
          <div className="flex min-w-0 items-center gap-3.5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-card bg-accent-500 text-black">
              <KeyRound size={20} strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                Existing company
              </div>
              <h2 className="m-0 mt-0.5 text-[22px] font-bold leading-tight tracking-[-0.01em] text-ink">
                Join your company
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line-strong bg-white text-ink transition-colors hover:bg-canvas-inset"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="m-0 text-[13px] leading-[21px] text-ink-dim">
            Use the work email and invitation code sent by your company.
          </p>

          <label className="mt-4 block">
            <span className="block text-[13px] font-semibold text-ink">Work email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoFocus
              className="mt-2 w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[14px] text-ink outline-none placeholder:text-ink-soft focus:border-ink"
            />
          </label>

          <label className="mt-4 block">
            <span className="block text-[13px] font-semibold text-ink">Invitation code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter invitation code"
              className="mt-2 w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[14px] text-ink outline-none placeholder:text-ink-soft focus:border-ink"
            />
            <span className="mt-1.5 block text-[12px] text-ink-soft">
              Pasting the whole invite link works too.
            </span>
          </label>

          {error && (
            <p className="m-0 mt-4 rounded-card bg-danger-soft px-4 py-3 text-[12.5px] leading-[19px] text-ink">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={review}
            disabled={busy}
            className="mt-5 inline-flex h-[46px] w-full items-center justify-center gap-2 rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50 whitespace-nowrap"
          >
            {busy && 'Checking…'}
            {!busy && (
              <>
                Review invitation <ArrowRight size={15} />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setNoCode((o) => !o)}
            aria-expanded={noCode}
            className="mt-3 h-11 w-full rounded-act text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset"
          >
            I do not have a code
          </button>

          {noCode && (
            <p className="m-0 mt-1 text-center text-[12.5px] leading-[19px] text-ink-dim">
              Only your company can issue one. Ask whoever owns the workspace to invite this address, or
              set up a company of your own from the page behind this.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const DirectEntry: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // A Solution Partner's client link. Carried into setup so the company lands under them.
  const referral = params.get('ref');
  const [joinOpen, setJoinOpen] = useState(false);

  return (
    <div className="min-h-screen bg-canvas">
      <header className="mx-auto flex max-w-[1240px] items-center px-6 py-7 md:px-10">
        <img src="/logo-black.png" alt="Agents Anywhere" className="h-7 w-auto object-contain" />
      </header>

      <main className="mx-auto grid max-w-[1240px] items-center gap-12 px-6 pb-10 pt-10 md:px-10 lg:grid-cols-[minmax(0,1fr)_520px] lg:gap-16 lg:pt-24">
        <div className="min-w-0">
          <span className="grid h-[70px] w-[70px] place-items-center rounded-[18px] bg-accent-500 text-black">
            <Sparkles size={32} strokeWidth={2} />
          </span>

          <div className="mt-8 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
            Agents Anywhere setup
          </div>
          <h1 className="m-0 mt-3 text-[46px] font-bold leading-[1.04] tracking-[-0.03em] text-ink sm:text-[60px]">
            Start with one company goal.
          </h1>
          <p className="m-0 mt-5 text-[16px] leading-[26px] text-ink-muted">
            Orbit guides the setup, prepares the right questions, and keeps every invitation under your
            control.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(referral ? `/signup?ref=${encodeURIComponent(referral)}` : '/signup')}
              className="inline-flex h-[54px] items-center gap-2.5 rounded-act bg-ink px-7 text-[14.5px] font-semibold text-white transition-colors hover:bg-black whitespace-nowrap"
            >
              Set up a company <ArrowRight size={17} />
            </button>
            <button
              type="button"
              onClick={() => setJoinOpen(true)}
              className="inline-flex h-[54px] items-center rounded-act border-2 border-ink bg-white px-7 text-[14.5px] font-semibold text-ink transition-colors hover:bg-canvas-inset whitespace-nowrap"
            >
              Join your company
            </button>
          </div>
          <p className="m-0 mt-4 text-[12.5px] text-ink-dim">
            A short setup, a few minutes. Skip what you do not know and pick up later.
          </p>
        </div>

        <OrbitSteps />
      </main>

      {joinOpen && <JoinCompany onClose={() => setJoinOpen(false)} />}
    </div>
  );
};

export default DirectEntry;
